import { formatCurrency } from '../../../utils/formatUtils';
import { toISODate } from '../../utils/financeFormatters';
import {
  ACCOUNTING_ORIGIN_LABELS,
  DASHBOARD_CHART_COLORS,
} from '../constants/accountingEntriesConstants';

const monthLabels = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export const emptyLine = () => ({
  account_label: '',
  debit_amount: '',
  credit_amount: '',
});

export const createEmptyAccountingForm = () => ({
  entry_date: toISODate(),
  description: '',
  status: 'draft',
  lines: [emptyLine(), emptyLine()],
});

export const parseAccountingAmount = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const cleaned = String(value).trim().replace(/\s/g, '').replace(/[^0-9,.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === ',') return 0;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  let normalized = cleaned;

  if (lastComma >= 0 && lastDot >= 0) {
    normalized = lastComma > lastDot
      ? cleaned.replace(/\./g, '').replace(',', '.')
      : cleaned.replace(/,/g, '');
  } else if (lastComma >= 0) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (lastDot >= 0) {
    const parts = cleaned.split('.');
    const looksLikeThousands = parts.length > 1 && parts.slice(1).every((part) => part.length === 3);
    normalized = looksLikeThousands ? cleaned.replace(/\./g, '') : cleaned;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const formatAccountingAmountInput = (value) => {
  const parsed = parseAccountingAmount(value);
  return parsed ? String(parsed) : '';
};

export const roundMoney = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const sumAccountingLines = (lines = []) =>
  lines.reduce(
    (acc, line) => ({
      debit: roundMoney(acc.debit + parseAccountingAmount(line.debit_amount)),
      credit: roundMoney(acc.credit + parseAccountingAmount(line.credit_amount)),
    }),
    { debit: 0, credit: 0 },
  );

export const humanizeTechnicalValue = (value) => {
  if (!value) return '-';
  return String(value)
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const extractAccountingErrorMessage = (error) => {
  const raw = error?.response?.data?.error || error?.response?.data?.message || error?.message;
  if (!raw || typeof raw !== 'string') return 'No se pudo completar la operación';
  const match = raw.match(/Value error,\s*([^[]+)/i);
  return match?.[1]?.trim() || raw;
};

export const getAccountingOriginLabel = (sourceType) =>
  ACCOUNTING_ORIGIN_LABELS[sourceType] || humanizeTechnicalValue(sourceType);

export const getDashboardChartColor = (index) =>
  DASHBOARD_CHART_COLORS[index % DASHBOARD_CHART_COLORS.length];

export const formatCompactAmount = (value) => {
  const number = Number(value || 0);
  const abs = Math.abs(number);
  if (abs >= 1000000) return `$ ${(number / 1000000).toLocaleString('es-AR', { maximumFractionDigits: 1 })} M`;
  if (abs >= 1000) return `$ ${(number / 1000).toLocaleString('es-AR', { maximumFractionDigits: 0 })} k`;
  return formatCurrency(number, 2);
};

export const truncateChartLabel = (value, maxLength = 20) => {
  const label = String(value || '-');
  return label.length > maxLength ? `${label.slice(0, maxLength - 3)}...` : label;
};

export const getEntryMonthKey = (entry) => {
  const rawDate = entry?.entry_date;
  if (!rawDate) return null;
  const date = new Date(`${String(rawDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  const month = date.getMonth();
  const year = date.getFullYear();
  return {
    key: `${year}-${String(month + 1).padStart(2, '0')}`,
    label: `${monthLabels[month]} ${String(year).slice(-2)}`,
  };
};

export const buildAccountingEntryPayload = (form) => ({
  entry_date: form.entry_date,
  description: form.description.trim(),
  status: form.status,
  lines: form.lines.map((line, index) => ({
    line_number: index + 1,
    account_label: line.account_label.trim(),
    debit_amount: roundMoney(parseAccountingAmount(line.debit_amount)),
    credit_amount: roundMoney(parseAccountingAmount(line.credit_amount)),
  })),
});

export const validateAccountingEntryForm = (form) => {
  if (!form.entry_date) return 'La fecha es obligatoria.';
  if (!form.description.trim() || form.description.trim().length < 3) {
    return 'La descripción debe tener al menos 3 caracteres.';
  }
  if (form.lines.length < 2) return 'El asiento debe tener al menos dos líneas.';

  for (const [index, line] of form.lines.entries()) {
    const rowNumber = index + 1;
    const debit = parseAccountingAmount(line.debit_amount);
    const credit = parseAccountingAmount(line.credit_amount);
    if (!line.account_label.trim()) return `La línea ${rowNumber} no tiene cuenta.`;
    if (debit < 0 || credit < 0) return `La línea ${rowNumber} tiene un importe negativo.`;
    if (debit > 0 && credit > 0) return `La línea ${rowNumber} no puede tener Debe y Haber a la vez.`;
    if (debit === 0 && credit === 0) return `La línea ${rowNumber} debe tener importe en Debe o Haber.`;
  }

  const totals = sumAccountingLines(form.lines);
  if (roundMoney(totals.debit - totals.credit) !== 0) return 'El asiento debe balancear Debe y Haber.';
  return '';
};

export const buildAccountingLedgerRows = (entries = []) => {
  const grouped = new Map();
  entries
    .filter((entry) => entry.status === 'posted')
    .forEach((entry) => {
      (entry.lines || []).forEach((line) => {
        const key = line.account_label || 'Sin cuenta';
        const current = grouped.get(key) || { account_label: key, debit: 0, credit: 0 };
        current.debit = roundMoney(current.debit + parseAccountingAmount(line.debit_amount));
        current.credit = roundMoney(current.credit + parseAccountingAmount(line.credit_amount));
        grouped.set(key, current);
      });
    });

  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      balance: roundMoney(row.debit - row.credit),
    }))
    .sort((left, right) => left.account_label.localeCompare(right.account_label));
};
